import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDownloadURL, getMetadata, ref } from 'firebase/storage';

import Layout from '../../components/Layout/Layout';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import ScreenshotModal from '../../components/ui/ScreenshotModal';
import DeviceLogs from './Component/DeviceLogs';
import ProtectionsPanel from '../../components/ProtectionsPanel';
import { adminApi } from '../../config/axios';
import { storage, authReady } from '../../config/firebase';
import { useGlobal } from '../../contexts/GlobalContext';
import { dateFormat, dateTimeFormat, useDocumentTitle } from '../../config/utils';
import HealthSection from '../../components/health/HealthSection';
import HdmiMode from '../../components/health/HdmiMode';
import DeviceLogDownload from '../../components/devices/DeviceLogDownload';
import DeviceSettingsCard from '../../components/devices/DeviceSettingsCard';
import { relativeSince } from '../../utils/relativeSince';
import { connectivityBadge } from '../../utils/connectivityBadge';
import { tzLabel, tzMismatch, tzDriftPhrase } from '../../config/timezones';

export default function DeviceDetail() {
  const { id }        = useParams();
  const navigate      = useNavigate();
  const { showToast } = useGlobal();

  const [device, setDevice] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [screenshotAt, setScreenshotAt]   = useState(null);
  const [screenshotErr, setScreenshotErr] = useState(false);
  const [screenshotModal, setScreenshotModal] = useState(false);
  const [msgModal, setMsgModal] = useState(false);
  const [message, setMessage]   = useState('');

  const [remoteAccess, setRemoteAccess] = useState(null);   // visitor config + commands
  const [showVisitorCfg, setShowVisitorCfg] = useState(false);

  // 1) Fetch device info, then keep polling so Online/Offline stays in sync
  // with the tenant dashboard (which polls every 15s) instead of the one-shot
  // fetch this page used to do, which never updated after the initial load.
  //
  // Unified-presence rollout, part 2 (2026-10-06): this poll is now the
  // ONLY status refresh mechanism on this page — no RTDB listener for
  // presence or remote-access exists anymore. `device.connectivity` (state +
  // remote_access) comes from `/device/{id}/info` -> Admin\DeviceController
  // ::info() -> Device::resolveConnectivity(), which is already fresh by
  // construction for a new-player build (same heartbeat that proves the
  // device alive carries foreground/standby/remote_access_connected) and,
  // for an old-player build, does the one-time RTDB refinement server-side
  // instead of this page doing it client-side.
  useEffect(() => {
    let cancelled = false;

    const fetchDevice = (isInitial) => {
      adminApi.get(`/device/${id}/info`)
        .then(res => { if (!cancelled) setDevice(res.data?.data); })
        .catch(() => { if (!cancelled && isInitial) showToast('error', 'Error', 'Could not load device info.'); })
        .finally(() => { if (!cancelled && isInitial) setLoading(false); });
    };

    fetchDevice(true);
    const interval = setInterval(() => fetchDevice(false), 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [id]);

  // 1b) Fetch Remote Access (frp STCP) visitor config + connect commands —
  // static config (ADB/scrcpy command strings, visitor.toml), not a live
  // status signal. Flat payload (backend returns response()->json([...]),
  // not a Resource).
  useEffect(() => {
    adminApi.get(`/device/${id}/remote-access`)
      .then(res => setRemoteAccess(res.data))
      .catch(() => { /* non-fatal: card just stays empty */ });
  }, [id]);

  // Unified-presence rollout, part 2 (2026-10-06): on/off, standby/
  // app_closed, and remote-access are ALL `device.connectivity` exactly as
  // the backend returns it — no RTDB, no client-side threshold/freshness
  // logic anywhere on this page. `presence_status` (content_corrupted/
  // scheduled_off) is layered on top exactly like the tenant app's
  // resolveDeviceStatus(), since those are content/schedule facts outside
  // connectivity.state's 4-value enum by design, not RTDB-derived.
  const connectivity = device?.connectivity ?? {};
  const status = ['content_corrupted', 'scheduled_off'].includes(device?.presence_status)
    ? device.presence_status
    : (connectivity.state ?? (device?.is_online ? 'online' : 'offline'));
  const pill = connectivityBadge(status);
  const tunnelConnected = connectivity.remote_access === 'connected';

  const copyText = useCallback((text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text)
      .then(() => showToast('success', 'Copied', 'Copied to clipboard.'))
      .catch(() => showToast('error', 'Failed', 'Could not copy.'));
  }, [showToast]);

  useDocumentTitle(device?.name ?? 'Device Preview');

  // 2) Fetch latest screenshot from Firebase Storage
  const loadScreenshot = useCallback(async () => {
    if (!device?.serial_name) return;
    try {
      await authReady; // Storage rules gate /screenshots/* on auth != null
      const imageRef = ref(storage, `screenshots/${device.serial_name}.png`);
      const [url, meta] = await Promise.all([
        getDownloadURL(imageRef),
        getMetadata(imageRef),
      ]);
      setScreenshotUrl(`${url}&_ts=${Date.now()}`);
      setScreenshotAt(meta?.updated ? new Date(meta.updated) : null);
      setScreenshotErr(false);
    } catch {
      setScreenshotUrl(null);
      setScreenshotAt(null);
      setScreenshotErr(true);
    }
  }, [device?.serial_name]);

  useEffect(() => { loadScreenshot(); }, [loadScreenshot]);

  // 3) Refresh screenshot when modal closes (the modal may have captured a new one)
  useEffect(() => {
    if (!screenshotModal) loadScreenshot();
  }, [screenshotModal, loadScreenshot]);

  const sendCommand = useCallback(async (cmd) => {
    try {
      await adminApi.post(`/device/${id}/command`, { command: cmd });
      showToast('success', 'Command Sent', `${cmd} dispatched.`);
    } catch {
      showToast('error', 'Error', 'Command failed.');
    }
  }, [id, showToast]);

  const resetClaim = useCallback(async () => {
    if (!window.confirm(
      'Reset this device for re-pairing?\n\n' +
      'This unclaims the device: company, owner, campaigns, and tokens are ' +
      'all wiped. The device will show a new 6-digit pairing code on its ' +
      'next boot, ready to be claimed by a new customer.\n\n' +
      'This is destructive. Use Reissue Token if the same owner just lost ' +
      'their local token.'
    )) return;
    try {
      const { data } = await adminApi.post(`/device/${id}/reset-claim`);
      showToast('success', 'Device Reset',
        data?.message ?? `New pairing code: ${data?.code ?? '—'}`);
    } catch (err) {
      showToast('error', 'Failed', err?.response?.data?.message ?? 'Could not reset device.');
    }
  }, [id, showToast]);

  const reissueToken = useCallback(async () => {
    if (!window.confirm(
      'Reissue authentication token for this device?\n\n' +
      'The current token will be revoked. The player will pick up the ' +
      'new token from Firebase RTDB within a few seconds; no reboot is required.'
    )) return;
    try {
      const { data } = await adminApi.post(`/device/${id}/reissue-token`);
      showToast('success', 'Token Reissued', data?.message ?? 'New token delivered via RTDB.');
    } catch (err) {
      showToast('error', 'Failed', err?.response?.data?.message ?? 'Could not reissue token.');
    }
  }, [id, showToast]);

  const sendMessage = useCallback(async () => {
    if (!message.trim()) return;
    try {
      await adminApi.post(`/device/${id}/message`, { message });
      showToast('success', 'Message Sent', 'Announcement queued on the device.');
      setMsgModal(false);
      setMessage('');
    } catch (err) {
      showToast('error', 'Failed', err?.response?.data?.message ?? 'Could not send message.');
    }
  }, [id, message, showToast]);

  const currentCampaign = device?.campaigns?.length > 0 ? device.campaigns[0] : null;
  // Unified-presence rollout (2026-10-06): prefer the backend's single
  // `connectivity.last_seen_at` (server-clock receive time) over the raw
  // `last_login` field — same instant today (last_login is already
  // server-clock, see Device::connectivity()), but this is the one field
  // every reader is meant to converge on.
  const heartbeat       = device?.connectivity?.last_seen_at ?? device?.last_login;
  const appVersion      = device?.app_version ?? health?.app_version ?? device?.device_info?.version_name ?? device?.device_info?.app_version;

  if (loading) return <Layout><div className="flex items-center justify-center py-16"><Spinner size="lg" /></div></Layout>;
  if (!device) return <Layout><p className="text-text-muted p-6">Device not found.</p></Layout>;

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted mb-5">
        <button onClick={() => navigate('/devices')} className="hover:text-text-main">Devices</button>
        <span>›</span>
        <span className="text-text-main">📺 Preview — {device.name ?? device.serial_name}</span>
      </div>

      {/* Header */}
      <div className="card-padded flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-text-main">{device.name ?? device.serial_name}</h1>
            <Badge variant={pill.variant} dot>{pill.label}</Badge>
          </div>
          <p className="text-text-muted text-sm mt-1">
            Serial: <code className="text-xs text-text-main">{device.serial_name}</code>
            <span className="mx-2">·</span>
            Company: <strong className="text-text-main">{device.company_name}</strong>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => sendCommand('restart')}     className="btn-secondary text-xs" title="Restart device · إعادة تشغيل">↺ Restart</button>
          <button onClick={() => sendCommand('clear_cache')} className="btn-secondary text-xs" title="Clear cache · مسح التخزين">⌫ Clear Cache</button>
          <button onClick={() => setMsgModal(true)}          className="btn-secondary text-xs" title="Send message · إرسال رسالة">✉ Send Message</button>
          <button onClick={reissueToken}                     className="btn-secondary text-xs" title="Reissue Sanctum token · re-mint device auth">🔑 Reissue Token</button>
          <button onClick={resetClaim}                       className="btn-secondary text-xs text-danger" title="Reset device · unclaim for re-pairing (destructive)">♻ Reset Device</button>
          <DeviceLogDownload deviceId={id} serial={device.serial_name} />
        </div>
      </div>

      {/* Device Health (step 1) */}
      <HealthSection deviceId={id} onLoaded={setHealth} />

      {/* Install-team settings (moved here from the customer device page) */}
      <div className="mb-6"><DeviceSettingsCard deviceId={id} settings={device.device_settings} /></div>

      {/* Location & clock — support visibility for "why did this screen fire at
          the wrong time". The branch assignment and the resolved zone are shown
          as SEPARATE facts: a device can sit on a branch whose own timezone is
          null, in which case the clock comes from the company while the device
          is still assigned to that branch. */}
      <div className="card-padded mb-6">
        <h3 className="text-base font-semibold text-text-main mb-3">Location &amp; clock</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-text-muted text-xs uppercase tracking-wide mb-1">Branch</p>
            {device.branch_name ? (
              <p className="text-text-main font-medium">{device.branch_name}</p>
            ) : (
              <p className="text-text-muted">No branch — using the company setting</p>
            )}
            <p className="text-text-muted text-xs mt-1">
              Set from the tenant dashboard (Devices → edit). Read-only here.
            </p>
          </div>
          <div>
            <p className="text-text-muted text-xs uppercase tracking-wide mb-1">Effective timezone</p>
            <p className="text-text-main font-medium">
              {device.resolved_timezone ? tzLabel(device.resolved_timezone) : '—'}
            </p>
            <p className="text-text-muted text-xs mt-1">
              {device.timezone_source === 'branch'
                ? `from branch “${device.timezone_source_name}”`
                : device.timezone_source === 'company'
                  ? 'company setting'
                  : device.timezone_source === 'fallback'
                    ? 'default fallback (not configured)'
                    : '—'}
            </p>
          </div>
        </div>

        {(() => {
          // The player fires its schedules off its OWN clock, so a screen in the
          // wrong zone runs every schedule at the wrong time, forever.
          const issue = tzMismatch(device.device_info?.timezone, device.resolved_timezone);
          if (!issue) return null;

          if (issue.kind === 'unknown') {
            return (
              <p className="mt-3 text-xs text-text-muted border-t border-border pt-3">
                This player doesn’t report its clock — any build older than v1.8.43. Upgrade it to
                see clock drift here.
              </p>
            );
          }

          return (
            <div className="mt-3 border-t border-border pt-3">
              <p className="text-sm text-danger font-medium">
                ⚠ Device clock disagrees with its venue
              </p>
              <p className="text-xs text-text-muted mt-1">
                Screen is on <strong className="text-text-main">{issue.deviceZone}</strong>{' '}
                ({issue.deviceOffsetLabel}), venue is{' '}
                <strong className="text-text-main">{issue.venueZone}</strong>{' '}
                ({issue.venueOffsetLabel}) — schedules fire{' '}
                <strong className="text-text-main">{tzDriftPhrase(issue.deltaMinutes)}</strong>.
                Fix it in the device’s Android date &amp; time settings.
              </p>
            </div>
          );
        })()}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Current Screen */}
        <div className="lg:col-span-2 card-padded">
          <div className="flex items-center justify-between mb-3">
            <h3 className="section-title">📸 Current Screen</h3>
            <button
              onClick={() => setScreenshotModal(true)}
              className="btn-primary text-xs"
              title="Capture new screenshot · لقطة شاشة"
            >
              🔄 Capture New
            </button>
          </div>
          <div className="aspect-video bg-black/40 rounded-xl flex items-center justify-center border border-white/[0.06] overflow-hidden">
            {screenshotUrl ? (
              <img src={screenshotUrl} alt="Current screen" className="w-full h-full object-contain" />
            ) : screenshotErr ? (
              <div className="text-center text-text-muted text-sm px-6">
                <p>No screenshot has been captured yet.</p>
                <p className="mt-1">Click <strong>Capture New</strong> to take one.</p>
              </div>
            ) : (
              <Spinner size="lg" />
            )}
          </div>
          {screenshotAt && (
            <p className="text-xs text-text-muted mt-2">
              Last captured: <span title={screenshotAt.toLocaleString()}>{relativeSince(screenshotAt)}</span>
            </p>
          )}
        </div>

        {/* Device info */}
        <div className="card-padded flex flex-col gap-4">
          <h3 className="section-title">Device Info</h3>
          <InfoRow label="Status">
            <Badge variant={pill.variant} dot>{pill.label}</Badge>
          </InfoRow>
          <InfoRow label="Serial">
            <code className="text-[11px] text-text-main">{device.serial_name}</code>
          </InfoRow>
          <InfoRow label="Campaign">{currentCampaign?.name ?? <span className="text-text-muted/70">No active show</span>}</InfoRow>
          <InfoRow label="Last Seen" title={heartbeat ? dateTimeFormat(heartbeat) : undefined}>
            {heartbeat ? relativeSince(heartbeat) : '—'}
          </InfoRow>
          <InfoRow label="Rotation">{device.rotation ?? 0}°</InfoRow>
          {appVersion        && <InfoRow label="App Version">{appVersion}</InfoRow>}
          <InfoRow label="HDMI Output"><HdmiMode mode={device.hdmi_output_mode} /></InfoRow>
          {/* build 396: whether the unit reported itself rooted -- a full-device reboot (restart_device) only works on a rooted unit */}
          <InfoRow label="Rooted">{rootedLabel(device.device_info?.rooted)}</InfoRow>
          <InfoRow label="Added">{dateFormat(device.created_at)}</InfoRow>
          {device.ram_usage     && <InfoRow label="RAM">{device.ram_usage}</InfoRow>}
          {device.storage_usage && <InfoRow label="Storage">{device.storage_usage}</InfoRow>}
          {device.uptime        && <InfoRow label="Uptime">{device.uptime}</InfoRow>}
        </div>
      </div>

      {/* Remote Access — frp STCP tunnel. No public ADB port; support runs the
          visitor with the per-device secret, then adb/scrcpy to 127.0.0.1. */}
      <div className="card-padded mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title">🖥 Remote Access</h3>
          <Badge variant={tunnelConnected ? 'teal' : 'muted'} dot>
            {tunnelConnected ? 'Tunnel active' : 'Tunnel offline'}
          </Badge>
        </div>
        <p className="text-text-muted text-sm mb-4">
          Reach this device over an encrypted tunnel — no on-site visit. Run the visitor once,
          then connect with ADB or scrcpy. The per-device secret is the only thing that authorizes a connection.
        </p>

        {remoteAccess ? (
          <div className="space-y-3">
            <CommandRow label="Connect (ADB)" value={remoteAccess.adb_command} onCopy={copyText} />
            <CommandRow label="View screen (scrcpy)" value={remoteAccess.scrcpy_command} onCopy={copyText} />

            <button
              onClick={() => setShowVisitorCfg(v => !v)}
              className="text-xs font-semibold text-accent hover:underline"
            >
              {showVisitorCfg ? '▾' : '▸'} First-time setup (run the relay visitor)
            </button>
            {showVisitorCfg && (
              <div>
                <div className="flex items-stretch gap-2">
                  <pre className="flex-1 min-w-0 overflow-auto max-h-48 input-base text-[11px] leading-relaxed m-0 whitespace-pre">{remoteAccess.visitor_toml}</pre>
                  <button className="btn-secondary text-xs shrink-0" onClick={() => copyText(remoteAccess.visitor_toml)}>📋 Copy</button>
                </div>
                <p className="text-text-muted text-xs mt-2">
                  Save as <code className="text-text-main">visitor.toml</code> and run
                  <code className="text-text-main mx-1">frpc -c visitor.toml</code>
                  — then use the ADB command above.
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-text-muted text-sm">Loading connection details…</p>
        )}
      </div>

      {/* Protections — per-device overrides of the self-healing kill switches + HDMI-IN audio delay */}
      <div className="card-padded mb-4">
        <h3 className="section-title mb-1">Protections</h3>
        <p className="text-text-muted text-sm mb-3">Effective value and where it comes from. Setting a value here is a device override.</p>
        <ProtectionsPanel scope="device" scopeId={id} />
      </div>

      {/* Campaigns list */}
      {device?.campaigns?.length > 0 && (
        <div className="card-padded mb-4">
          <h3 className="section-title mb-4">Assigned Campaigns</h3>
          <div className="space-y-2">
            {device.campaigns.map(c => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b border-white/[0.05] last:border-0">
                <span className="text-sm text-text-main">{c.name}</span>
                <Badge variant="accent">{c.status?.toLowerCase() ?? 'active'}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Device logs — paginated feed from device_logging + device_errors_logging.
          Filterable by LOG/ERROR/ALL. Used by support to diagnose player issues. */}
      <DeviceLogs deviceId={id} />

      {/* Screenshot modal — pass the device through so capture works without
          needing a second info fetch. */}
      <ScreenshotModal
        open={screenshotModal}
        device={device}
        onClose={() => setScreenshotModal(false)}
      />

      {/* Send message modal — lightweight inline version since the device is
          already loaded in this page. */}
      {msgModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMsgModal(false)} />
          <div className="relative w-full max-w-md card">
            <div className="px-6 py-4 border-b border-white/[0.08]">
              <h3 className="text-base font-semibold text-text-main">Send Message to {device.name ?? device.serial_name}</h3>
            </div>
            <div className="px-6 py-4">
              <textarea
                className="input-base h-28 resize-none"
                placeholder="Type your message — the device will speak it aloud…"
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </div>
            <div className="px-6 py-4 border-t border-white/[0.08] flex justify-end gap-3">
              <button className="btn-secondary" onClick={() => setMsgModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={sendMessage}>Send</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/** device_info.rooted as the unit reported it: true/1/'1'/'true' = yes, false/0/'0'/'false' = no, absent = not reported yet (counts as NOT rooted for reboots). */
export function rootedLabel(v) {
  if (v === true || v === 1 || v === '1' || v === 'true') return 'Yes — can be rebooted remotely';
  if (v === false || v === 0 || v === '0' || v === 'false') return 'No';
  return 'Unknown — not reported yet';
}

function InfoRow({ label, children, title }) {
  return (
    <div className="flex items-center justify-between text-sm" title={title}>
      <span className="text-text-muted">{label}</span>
      <span className="text-text-main font-medium">{children}</span>
    </div>
  );
}

function CommandRow({ label, value, onCopy }) {
  return (
    <div>
      <span className="text-text-muted text-xs block mb-1">{label}</span>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 min-w-0 truncate input-base text-xs" title={value}>{value ?? '—'}</code>
        <button className="btn-secondary text-xs shrink-0" onClick={() => onCopy(value)}>📋 Copy</button>
      </div>
    </div>
  );
}
